import { Trend, BlogPost } from "ExtendiApp"
import { graphql } from "gatsby"
import { HelmetDatoCms } from "gatsby-source-datocms"
import React, { Fragment, useState } from "react"

import { CardBlogPost } from "src/components/CardBlogPost"
import { CardChart } from "src/components/CardChart"
import { CategorySelector } from "src/components/CategorySelector"
import { Footer } from "src/components/Footer"
import { HeaderShape } from "src/components/HeaderShape"
import { NewsletterSignup } from "src/components/NewsletterSignup"
import { TopLogo } from "src/components/TopLogo"
import { TwitterCards } from "src/components/TwitterCards"
import { Container, Row, Col, Button } from "src/ui"
import Lovie from "src/ui/img/aw1.png"
import Drum from "src/ui/img/aw22.png"
import { validatePost } from "src/utils/validatePost"
import { validateTrend } from "src/utils/validateTrend"

const Page = ({ data: { page, categoryEdges }, pageContext }) => {
  const { title, descriptionNode, seoMetaTags, trendTv } = page

  // categories logic
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const categories = categoryEdges.edges?.map(c => c.node.id)

  // trends logic
  const [showAllTrends, setShowAllTrends] = useState(false)
  const TRENDS_PAGINATION = 24
  const trends: Trend[] = pageContext.trends.filter(validateTrend)
  const trendsForActiveCategory = (trends || []).filter(trend =>
    !activeCategory
      ? true
      : trend.categories.some(cat => cat.id === activeCategory)
  )
  const trendsToShow = showAllTrends
    ? trendsForActiveCategory
    : trendsForActiveCategory.slice(0, TRENDS_PAGINATION)

  // blog logic
  const [showAllBlogPosts, setShowAllBlogPosts] = useState(false)
  const blogPosts: BlogPost[] = pageContext.blogPosts
    .filter(validatePost)
    .sort((a, b) => (a.date > b.date ? -1 : 1))
  const blogPostsToShow = showAllBlogPosts ? blogPosts : blogPosts.slice(0, 9)

  return (
    <Fragment>
      <HelmetDatoCms seo={seoMetaTags} />
      <div className="bg">
        <HeaderShape />
      </div>

      <TopLogo />

      <div className="section main-section">
        <Container className="position-relative">
          {/* HEADER */}
          <div className="claim text-center">
            <h1 className="text-white mb-4">{title}</h1>
            <div
              className="text-white display-3"
              dangerouslySetInnerHTML={{
                __html: descriptionNode.childMarkdownRemark.html,
              }}
            />
          </div>

          {/* CATEGORIES */}
          <div className="mb-5">
            <CategorySelector
              trends={trends}
              onCategoryChange={newCategory => {
                setActiveCategory(newCategory)
                setShowAllTrends(false)
              }}
              categoriesReferenceIdOrder={categories}
            />
          </div>

          {/* CHARTS */}
          <Row>
            {trendsToShow && trendsToShow.length
              ? trendsToShow.map(trend =>
                  trend.json ? (
                    <Col key={trend.id} md="4" className="mb-gutter">
                      <CardChart
                        topic={trend.topic}
                        description={trend.description || ""}
                        data={trend.json}
                        legend={[
                          trend.legendA || "",
                          trend.legendB || "",
                          trend.legendC || "",
                        ]}
                        url={trend.url}
                      />
                    </Col>
                  ) : null
                )
              : null}
          </Row>

          {trendsForActiveCategory.length > TRENDS_PAGINATION &&
          !showAllTrends ? (
            <Row>
              <Col className="text-center mt-5">
                <Button size="lg" onClick={() => setShowAllTrends(true)}>
                  View more
                </Button>
              </Col>
            </Row>
          ) : null}
        </Container>
      </div>

      {/* TWITTER */}
      <div className="section bg-primary-light">
        <Container>
          <div className="section-header text-center">
            <h2 className="pb-5 text-white">TrendsTV 📺</h2>
          </div>
          <TwitterCards
            tweets={trendTv.map(({ tweetId }) => tweetId).reverse()}
          />
        </Container>
      </div>

      {/* NEWSLETTER */}
      <div className="section bg-light newsletter">
        <Container>
          <div className="section-header text-center">
            <h2 className="pb-2">Stay in the Loop</h2>
            <p className="pb-5">
              Sign up to Pulsar updates, including a #NewNormal digest{" "}
              <a href="https://www.pulsarplatform.com/privacy-policy/" rel="">
                (privacy policy)
              </a>
            </p>
          </div>
          <NewsletterSignup />
        </Container>
      </div>

      {/* BLOG POSTS */}
      <div className="section">
        <Container>
          <div className="section-header text-center">
            <h2 className="pb-5">Go deeper</h2>
          </div>
          <Row>
            {blogPostsToShow && blogPostsToShow.length
              ? blogPostsToShow.map(post => (
                  <Col key={post.id} md="4" className="mb-gutter">
                    <CardBlogPost postData={post} />
                  </Col>
                ))
              : null}
          </Row>

          {!showAllBlogPosts && blogPosts && blogPosts.length > 9 && (
            <div className="text-center">
              <Button size="lg" onClick={() => setShowAllBlogPosts(true)}>
                Show all
              </Button>
            </div>
          )}
        </Container>
      </div>

      <div className="bg-light">
        <Footer />
      </div>
      <div className="awards">
        <a
          href="https://www.pulsarplatform.com/blog/2020/pulsars-most-recent-award-noms/"
          target="_blank"
          rel="noopener noreferrer"
          title="The Lovie Awards Shortlisted – Websites: Best Writing – Editorial / Shortlisted – Websites: Lifestyle"
        >
          <img alt="" src={Lovie} width="40" />
        </a>
        <a
          href="https://www.pulsarplatform.com/blog/2020/pulsars-most-recent-award-noms/"
          target="_blank"
          rel="noopener noreferrer"
          title="The Drum Awards – Content Finalists – Best B2B Content Marketing Strategy/Campaign / Finalists – Best News/Media/Publishing Content Marketing Strategy/Campaign"
          style={{ backgroundColor: "#ec1c24" }}
        >
          <img alt="" src={Drum} width="40" />
        </a>
      </div>
    </Fragment>
  )
}

export default Page

export const query = graphql`
  query PageQuery($slug: String!) {
    page: datoCmsPage(slug: { eq: $slug }) {
      id
      title
      descriptionNode {
        childMarkdownRemark {
          html
        }
      }
      trendTv {
        tweetId
      }
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }
    categoryEdges: allDatoCmsCategory(
      sort: { fields: [position], order: ASC }
    ) {
      edges {
        node {
          id
          displayName
        }
      }
    }
  }
`
